import React, { useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Chart, registerables } from 'chart.js';
import { startOfWeek, subWeeks, addWeeks, format, endOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Chip
} from '@mui/material';
import {
    CalendarToday,
    ChevronLeft,
    ChevronRight,
    Today
} from '@mui/icons-material';

Chart.register(...registerables);

const PerformanceChart = ({ responses, currentWeek, setCurrentWeek, hideWeekOptions }) => {
    const { t } = useTranslation();
    const [showDatePicker, setShowDatePicker] = useState(false);

    const handlePreviousWeek = () => {
        setCurrentWeek(subWeeks(currentWeek, 1));
    };

    const handleNextWeek = () => {
        setCurrentWeek(addWeeks(currentWeek, 1));
    };

    const handleCurrentWeek = () => {
        setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));
    };

    const handleDateChange = (event) => {
        const selectedDate = new Date(event.target.value);
        if (!isNaN(selectedDate)) {
            const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
            setCurrentWeek(weekStart);
            setShowDatePicker(false);
        }
    };

    const processResponsesToDataPoints = (responses) => {
        const points = [];

        // First pass: find min and max hours from the data
        let minHour = 23;
        let maxHour = 0;

        responses.forEach(response => {
            if (!response.score || !response.timestamp) {
                return;
            }

            const date = new Date(response.timestamp);
            const day = date.getDay();
            const hour = date.getHours();

            // Only consider weekdays (Mon-Fri)
            if (day >= 1 && day <= 5) {
                minHour = Math.min(minHour, hour);
                maxHour = Math.max(maxHour, hour);
            }
        });

        // If no data, default to 8am-8pm range
        if (minHour === 23 && maxHour === 0) {
            minHour = 8;
            maxHour = 20;
        }

        // Second pass: create data points
        responses.forEach(response => {
            if (!response.score || !response.timestamp) {
                return;
            }

            const date = new Date(response.timestamp);
            const day = date.getDay();
            const hour = date.getHours();

            if (day >= 1 && day <= 5 && hour >= minHour && hour <= maxHour) {
                const dayIndex = day - 1;
                const hourIndex = hour - minHour;
                points.push({
                    x: hourIndex,
                    y: dayIndex,
                    r: 9,
                    score: response.score,
                    minHour: minHour,
                    maxHour: maxHour,
                });
            }
        });

        return points;
    };

    const dataPoints = processResponsesToDataPoints(responses);

    // Extract minHour and maxHour from dataPoints
    const minHour = dataPoints.length > 0 ? dataPoints[0].minHour : 8;
    const maxHour = dataPoints.length > 0 ? dataPoints[0].maxHour : 20;

    const data = {
        datasets: [
            {
                label: 'Performance Score',
                data: dataPoints,
                backgroundColor: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0;
                    if (value === 0) return 'rgba(200, 200, 200, 0.1)';

                    const intensity = value / 10;
                    if (value <= 3) {
                        return `rgba(255, 99, 71, ${0.3 + intensity * 0.5})`;
                    } else if (value <= 6) {
                        return `rgba(255, 206, 86, ${0.3 + intensity * 0.5})`;
                    } else {
                        return `rgba(75, 192, 192, ${0.3 + intensity * 0.5})`;
                    }
                },
                borderColor: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0;
                    if (value === 0) return 'rgba(200, 200, 200, 0.3)';

                    if (value <= 3) return 'rgba(255, 99, 71, 0.8)';
                    else if (value <= 6) return 'rgba(255, 206, 86, 0.8)';
                    else return 'rgba(75, 192, 192, 0.8)';
                },
                borderWidth: 2,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                title: {
                    display: true,
                    text: t('performanceChart.timeOfDay'),
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: (value) => {
                        const hour = minHour + value;
                        if (hour < 0 || hour > 23) return '';
                        return hour <= 12 ? hour.toString() : (hour - 12).toString();
                    },
                    font: {
                        size: 10
                    },
                    maxRotation: 0,
                    autoSkip: false
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                }
            },
            y: {
                type: 'linear',
                title: {
                    display: true,
                    text: t('performanceChart.dayOfTheWeek'),
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: (value) => {
                        const dayLabels = [
                            t('performanceChart.mon'),
                            t('performanceChart.tue'),
                            t('performanceChart.wed'),
                            t('performanceChart.thu'),
                            t('performanceChart.fri')
                        ];
                        return dayLabels[value] || '';
                    },
                    font: {
                        size: 10
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                },
                reverse: true
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    generateLabels: () => [
                        {
                            text: 'Low (1-3)',
                            fillStyle: 'rgba(255, 99, 71, 0.6)',
                            strokeStyle: 'rgba(255, 99, 71, 0.8)',
                            lineWidth: 2
                        },
                        {
                            text: 'Medium (4-6)',
                            fillStyle: 'rgba(255, 206, 86, 0.6)',
                            strokeStyle: 'rgba(255, 206, 86, 0.8)',
                            lineWidth: 2
                        },
                        {
                            text: 'High (7-10)',
                            fillStyle: 'rgba(75, 192, 192, 0.6)',
                            strokeStyle: 'rgba(75, 192, 192, 0.8)',
                            lineWidth: 2
                        }
                    ]
                }
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const score = context.raw ? context.raw.score : 0;
                        const hour = minHour + context.parsed.x;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                        const time = `${displayHour} ${ampm}`;
                        const dayLabels = [
                            t('performanceChart.monday'),
                            t('performanceChart.tuesday'),
                            t('performanceChart.wednesday'),
                            t('performanceChart.thursday'),
                            t('performanceChart.friday')
                        ];
                        const day = dayLabels[context.parsed.y] || '';
                        return [
                            `${day} at ${time}`,
                            `Score: ${score}/10`
                        ];
                    }
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                cornerRadius: 4,
                displayColors: false
            },
        },
        elements: {
            point: {
                radius: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0;
                    return value ? 4 + (value / 10) * 6 : 3;
                },
                hoverRadius: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0;
                    return value ? 5 + (value / 10) * 7 : 4;
                },
            },
        },
    };

    // Format the current week range for display
    const weekStart = currentWeek;
    const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekRangeText = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;

    return (
        <Box className="performance-chart-container" sx={{ width: '100%', textAlign: 'center' }}>
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: '#333'
                }}
            >
                {t('performanceChart.performanceHeatmap')}
            </Typography>

            {!hideWeekOptions && (
                <Box sx={{ mb: 3 }}>
                    {/* Week Display with Calendar Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 2,
                            flexDirection: 'column',
                            gap: 1
                        }}
                    >
                        <Chip
                            label={weekRangeText}
                            icon={<CalendarToday />}
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            sx={{
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 123, 255, 0.1)'
                                }
                            }}
                        />

                        {showDatePicker && (
                            <input
                                type="date"
                                value={format(currentWeek, 'yyyy-MM-dd')}
                                onChange={handleDateChange}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    backgroundColor: '#fff',
                                    color: '#333',
                                    outline: 'none',
                                    transition: 'border-color 0.3s',
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#007BFF';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#ccc';
                                }}
                            />
                        )}
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                        className="week-navigation"
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <IconButton
                            onClick={handlePreviousWeek}
                            sx={{
                                border: '1px solid #007BFF',
                                color: '#007BFF',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 123, 255, 0.1)'
                                }
                            }}
                            size="small"
                        >
                            <ChevronLeft />
                        </IconButton>

                        <Button
                            onClick={handleCurrentWeek}
                            startIcon={<Today />}
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: '#007BFF',
                                '&:hover': {
                                    backgroundColor: '#0056b3'
                                },
                                textTransform: 'none',
                                px: 2
                            }}
                        >
                            {t('performanceChart.currentWeek')}
                        </Button>

                        <IconButton
                            onClick={handleNextWeek}
                            sx={{
                                border: '1px solid #007BFF',
                                color: '#007BFF',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 123, 255, 0.1)'
                                }
                            }}
                            size="small"
                        >
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </Box>
            )}

            <Box sx={{ height: '300px', width: '100%', position: 'relative', maxWidth: '500px', margin: '0 auto' }}>
                <Scatter data={data} options={options} />
            </Box>

            {dataPoints.length === 0 && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        color: 'text.secondary',
                        fontStyle: 'italic'
                    }}
                >
                    {t('performanceChart.noDataAvailable')}
                </Typography>
            )}
        </Box>
    );
};

export default PerformanceChart;